import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { Observable } from 'zen-observable-ts';
import AppContext from '../AppContext';
import { AddLicenseSystemInput, AddLicenseSystemPayload, CompleteAddLicenseSystemPayload } from './generated/types';
import { mapSubscriptionResponseToViewModel } from './mappers/licenseSystem';
import { LicenseSystemSubscriptionViewModel } from 'src/viewModels/LicenseSystem';

const mutation = `mutation AddLicenseSystem($input: AddLicenseSystemInput!) {
    addLicenseSystem(input: $input) {
        succeeded
        messageId
    }
}`;

const onCompleteAddLicenseSystem = `subscription OnCompleteAddLicenseSystem($messageId: String!) {
    onCompleteAddLicenseSystem(messageId: $messageId) {
        messageId
        data
        licenseSystemId
        error {
            errorMessage
            errorType
        }
    }
  }`;

interface AddLicenseSystemSubscription {
    data: {
        onCompleteAddLicenseSystem: CompleteAddLicenseSystemPayload;
    };
};


export const useAddNewLicenseSystemToOrg = (
    orgId: string,
    onMutationStart: () => void,
    onSubscriptionResponse: (args: LicenseSystemSubscriptionViewModel) => void,
) => {
    const context = useContext(AppContext);
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (file: string) => {

            onMutationStart();

            const input: AddLicenseSystemInput = { orgId, file };

            const { data } = await (context.api.graphql({
                query: mutation,
                variables: { input },
            }) as Promise<{
                data: {
                    addLicenseSystem: AddLicenseSystemPayload;
                };
            }>);

            return data.addLicenseSystem;
        },
        onSuccess: (data, _variables, _ctx) => {

            const qraphqlObserver = context.api.graphql({
                query: onCompleteAddLicenseSystem,
                variables: { messageId: data.messageId },
            }) as unknown as Observable<AddLicenseSystemSubscription>;

            const subscription = qraphqlObserver.subscribe({
                next: (payload) => {
                    const response = payload.data.onCompleteAddLicenseSystem;

                    // Handle response
                    onSubscriptionResponse(mapSubscriptionResponseToViewModel(response));

                    // invalidateQueries
                    void queryClient.invalidateQueries({ queryKey: ['getOrganization', orgId] });

                    subscription.unsubscribe();
                },
                error: (error) => {
                    console.error('Subscription error:', error);
                    subscription.unsubscribe();
                },
            });
        },
    });
};